import { css } from '@emotion/react';
import { Appointment as PersonAppointment } from '@weave/schema-gen-ts/dist/schemas/appointments/v3/model.pb';
import { Appointment as CalendarAppointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from '@frontend/i18n';
// SP-TODO: move this hook to schedule-helpers
import { useGetAppointmentDetails } from '@frontend/schedule';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Chip, Text } from '@frontend/design-system';

dayjs.extend(utc);
dayjs.extend(minMax);

interface PreparedAppointment {
  data: PersonAppointment | null;
  label: string | Date;
}

interface AppointmentDetailsProps {
  appointment: PreparedAppointment;
  locationId: string;
}

export const AppointmentDetails = ({ appointment, locationId }: AppointmentDetailsProps) => {
  const { t } = useTranslation('schedule');

  const { getScopeName, selectedLocationIds } = useAppScopeStore();

  const isMultiLocation = selectedLocationIds.length > 1;

  const locationName = getScopeName(locationId) ?? '';

  const convertedAppointment =
    appointment.data &&
    (Array(appointment.data).map((appointment) => ({
      id: appointment.appointmentId,
      start: appointment.appointmentAt,
      duration: appointment.length,
      statusOfficeView: appointment.status,
      externalStatus: appointment.status,
      locationId: locationId,
      practitionerName: appointment.practitioner || '',
      type: appointment.types?.map((type) => type).join(', ') || '',
      externalId: appointment.externalId,
      person: {
        personId: appointment.personId,
      },
    })) as CalendarAppointment);

  const { openModal: openAppointmentModal, Modal: AppointmentDetailsModal } = useGetAppointmentDetails({
    appointmentId: appointment?.data?.appointmentId ? appointment.data.appointmentId : '',
    locationId: locationId ?? '',
    appointments: convertedAppointment ? [convertedAppointment] : [],
    enabled: appointment.data !== null && !!appointment?.data?.appointmentId,
  });

  const timeRange =
    appointment.data && appointment.data.length
      ? `${dayjs(appointment.label).format('h:mm A')} - ${dayjs(appointment.label)
          .add(appointment.data?.length ?? 0, 'minute')
          .format('h:mm A')}`
      : dayjs(appointment.label).format('h:mm A');

  if (!appointment.data) return null;

  return (
    <>
      {AppointmentDetailsModal}
      <Text as='span' size='medium' weight='light' color='subdued'>
        {t('Next Appointment')}
      </Text>
      <section css={appointmentCardStyles} onClick={openAppointmentModal}>
        <Text as='span' size='medium' weight='bold' css={{ gridColumn: '1 / 3' }}>
          {appointment.label ? dayjs(appointment.label).format('MMMM D, YYYY') : '-'}
        </Text>
        <Text as='span' size='medium' weight='light' css={{ gridColumn: '1 / 3', gridRow: '2 / 3' }}>
          {timeRange}
        </Text>
        {isMultiLocation && (
          <Chip.Location variant='primary' css={{ gridColumn: '2 / 3', gridRow: '2 / 3', justifySelf: 'end' }}>
            {locationName}
          </Chip.Location>
        )}
      </section>
    </>
  );
};

const appointmentCardStyles = css({
  borderLeft: '4px solid',
  borderColor: theme.colors.warning50,
  background: theme.colors.white,
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(1),
  margin: theme.spacing(1, 0),
  boxShadow: theme.shadows.light,
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gridTemplateRows: '1fr 1fr',
  gap: theme.spacing(0.5),
  cursor: 'pointer',
  ':hover': {
    boxShadow: theme.shadows.floating,
    scale: 1.01,
  },
});
